import { useEffect, useRef, useState } from "react";
import { createMemoryTakeStore, RECORDING_POLICY } from "../../domain/recording";
import { speakEnglish } from "./audio";

export function VoiceCompare({ word, ipa }: { word: string; ipa?: string }) {
  const store = useRef(createMemoryTakeStore(RECORDING_POLICY.maxTakes));
  const recRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "rec" | "ready" | "denied" | "skip">("idle");
  const [self, setSelf] = useState("");

  useEffect(() => {
    return () => {
      store.current.clear();
      recRef.current?.stream.getTracks().forEach((t) => t.stop());
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  async function start() {
    if (store.current.count() >= RECORDING_POLICY.maxTakes) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunks.current.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/webm" });
        const next = store.current.add(blob, (b) => URL.createObjectURL(b));
        setUrl(next);
        setStatus("ready");
      };
      recRef.current = rec;
      rec.start();
      setStatus("rec");
      timer.current = window.setTimeout(() => stop(), RECORDING_POLICY.maxSeconds * 1000);
    } catch {
      setStatus("denied");
    }
  }

  function stop() {
    if (timer.current) window.clearTimeout(timer.current);
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  }

  function reset() {
    store.current.clear();
    setUrl(null);
    setSelf("");
    setStatus("idle");
  }

  if (status === "skip") {
    return <p className="muted">Можно продолжить без микрофона.</p>;
  }

  return (
    <div className="voice">
      <p>Послушай эталон, запиши короткую попытку и сравни звучание.</p>
      <p className="muted">Запись остаётся только в текущем упражнении. Clever не распознаёт и не оценивает произношение автоматически.</p>
      <div className="tools">
        <button type="button" className="btn ghost" onClick={() => speakEnglish(word)}>Послушать эталон</button>
        <button type="button" className="btn ghost" onClick={() => speakEnglish(word, 0.55)}>Послушать медленнее</button>
        {status !== "rec" ? (
          <button type="button" className="btn" onClick={() => void start()}>Начать запись</button>
        ) : (
          <button type="button" className="btn steel" onClick={stop}>Остановить</button>
        )}
        <button type="button" className="btn ghost" onClick={() => setStatus("skip")}>Пройти без записи</button>
      </div>
      {ipa ? <p className="ipa-lg">{ipa}</p> : null}
      {status === "rec" ? <p>Идёт запись…</p> : null}
      {url ? (
        <div className="tools">
          <button type="button" className="btn ghost" onClick={() => new Audio(url).play()}>Послушать себя</button>
          <button type="button" className="btn ghost" onClick={reset}>Записать заново</button>
          <button type="button" className="btn ghost" onClick={reset}>Удалить запись</button>
        </div>
      ) : null}
      {status === "denied" ? (
        <p>Браузер не дал доступ к микрофону. Продолжим без записи.</p>
      ) : null}
      {status === "ready" ? (
        <div>
          <p>Как тебе звучит?</p>
          <div className="tools">
            {["Похоже на эталон", "Хочу попробовать ещё", "Послушать вместе со взрослым"].map((label) => (
              <button key={label} type="button" className={`btn ghost${self === label ? " on" : ""}`} onClick={() => setSelf(label)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
